import { useFollowedSite } from '../../../contexts/SiteContext';
import { useEffect, useState } from 'react';
import { Bell, Play, CheckCircle2, AlertCircle, AlertTriangle, Info, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Input } from '@verlo/shared/components/ui/input';
import { Button } from '@verlo/shared/components/ui/button';
import { Switch } from '@verlo/shared/components/ui/switch';
import { reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';

const SEVERITY = {
  critical: { icon: AlertCircle,   cls: 'bg-signal-stop-tint text-signal-stop border-signal-stop-line', dot: 'bg-signal-stop-line' },
  warning:  { icon: AlertTriangle, cls: 'bg-signal-warn-tint text-signal-warn border-signal-warn-line', dot: 'bg-signal-warn-line' },
  info:     { icon: Info,          cls: 'bg-signal-info-tint text-signal-info border-signal-info-line', dot: 'bg-signal-info-line' },
};

const ReportsAlertsPage = () => {
  // '' = every site this user can read; the API omits the param (S1-4).
  const [site, setSite] = useFollowedSite('');
  const [sites, setSites] = useState([]);
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [evaluating, setEvaluating] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, e, s] = await Promise.all([
        reportsIntelApi.listAlertRules(),
        reportsIntelApi.listAlertEvents(statusFilter),
        reportsIntelApi.listSites(),
      ]);
      setRules(r.data.rules || []);
      setEvents(e.data.events || []);
      setSites(s.data.sites || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [statusFilter]);

  const evaluate = async () => {
    setEvaluating(true);
    try {
      const res = await reportsIntelApi.evaluateAlerts(site);
      toast.success(`Evaluated ${res.data.evaluated} rules · ${res.data.triggered.length} triggered`);
      await loadAll();
    } catch {
      toast.error('Evaluation failed');
    } finally {
      setEvaluating(false);
    }
  };

  const setRule = async (rule, patch) => {
    try {
      await reportsIntelApi.updateAlertRule(rule.id, patch);
      setRules((rs) => rs.map((r) => (r.id === rule.id ? { ...r, ...patch } : r)));
    } catch {
      toast.error('Could not update rule');
    }
  };

  const ack = async (id) => {
    await reportsIntelApi.ackAlertEvent(id);
    setEvents((es) => es.filter((e) => e.id !== id));
    toast.success('Acknowledged');
  };
  const resolve = async (id) => {
    await reportsIntelApi.resolveAlertEvent(id);
    setEvents((es) => es.filter((e) => e.id !== id));
    toast.success('Resolved');
  };

  return (
    <ReportsLayout>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <SitePicker sites={sites} value={site} onChange={setSite} allowAll allValue="" triggerClassName="h-9 w-[230px] bg-white border-hairline" testId="alerts-site-select" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[140px] bg-white border-hairline" data-testid="alerts-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={loadAll} data-testid="alerts-refresh-btn">
            <RefreshCw size={13} />
            Refresh
          </Button>
          <Button className="h-9 gap-1.5 bg-verlo hover:bg-verlo-hover" onClick={evaluate} disabled={evaluating} data-testid="alerts-evaluate-btn">
            <Play size={13} className={evaluating ? 'animate-pulse' : ''} />
            {evaluating ? 'Evaluating…' : 'Evaluate now'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        {/* Events feed */}
        <div className="xl:col-span-3 bg-white border border-hairline rounded-lg overflow-hidden flex flex-col" data-testid="alerts-events">
          <div className="px-5 py-4 border-b border-hairline flex items-center justify-between">
            <div>
              <h3 className="text-[13px] font-semibold text-ink">Active alerts</h3>
              <p className="text-[12.5px] text-ink-soft">{events.length} {statusFilter}</p>
            </div>
            <Bell size={14} className="text-ink-faint" />
          </div>
          {loading ? (
            <div className="p-6 text-center text-[12px] text-ink-faint">Loading…</div>
          ) : events.length === 0 ? (
            <div className="p-10 text-center text-[12px] text-ink-faint">
              <CheckCircle2 size={28} className="mx-auto mb-2 text-signal-ok" />
              {statusFilter === 'open' ? 'All clear. No alerts.' : 'No matching events.'}
            </div>
          ) : (
            <ul className="divide-y divide-hairline">
              {events.map((e) => {
                const meta = SEVERITY[e.severity] || SEVERITY.info;
                const Icon = meta.icon;
                return (
                  <li key={e.id} className="px-5 py-3 flex items-start gap-3" data-testid={`alert-event-${e.id}`}>
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.cls} shrink-0`}>
                      <Icon size={14} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-ink">{e.rule_name}</p>
                      <p className="text-[12.5px] text-ink-soft">
                        {e.kpi} {e.operator} {e.threshold} · current{' '}
                        <span className="font-mono-display font-semibold text-ink">{e.value}</span>
                      </p>
                      <p className="text-[11px] uppercase tracking-wider text-ink-faint mt-1 font-semibold">
                        {e.category} · {new Date(e.created_at).toLocaleString('en-GB')}
                      </p>
                    </div>
                    {e.status === 'open' && (
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 text-[12.5px]" onClick={() => ack(e.id)} data-testid={`alert-ack-${e.id}`}>Ack</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-[12.5px]" onClick={() => resolve(e.id)} data-testid={`alert-resolve-${e.id}`}>Resolve</Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Rules editor */}
        <div className="xl:col-span-2 bg-white border border-hairline rounded-lg overflow-hidden" data-testid="alerts-rules">
          <div className="px-5 py-4 border-b border-hairline">
            <h3 className="text-[13px] font-semibold text-ink">Threshold rules</h3>
            <p className="text-[12.5px] text-ink-soft">{rules.length} configured · toggle or adjust thresholds inline</p>
          </div>
          <ul className="divide-y divide-hairline max-h-[600px] overflow-y-auto">
            {rules.map((r) => {
              const meta = SEVERITY[r.severity] || SEVERITY.info;
              return (
                <li key={r.id} className="px-5 py-3 flex items-center gap-3" data-testid={`alert-rule-${r.id}`}>
                  <span className={`w-1 h-8 rounded ${meta.dot}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-semibold text-ink truncate">{r.name}</p>
                    <p className="text-[12.5px] text-ink-soft font-mono-display">
                      {r.kpi} {r.operator}{' '}
                      <Input
                        type="number"
                        defaultValue={r.threshold}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value);
                          if (!Number.isNaN(v) && v !== r.threshold) setRule(r, { threshold: v });
                        }}
                        className="inline-block w-20 h-6 text-[12.5px] py-0 px-1.5 ml-1"
                        data-testid={`alert-rule-threshold-${r.id}`}
                      />
                    </p>
                  </div>
                  <Switch
                    checked={r.enabled}
                    onCheckedChange={(v) => setRule(r, { enabled: v })}
                    data-testid={`alert-rule-toggle-${r.id}`}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </ReportsLayout>
  );
};

export default ReportsAlertsPage;
